// ecommerce-frontend/src/pages/Checkout.jsx
// Page de finalisation de commande avec déduction automatique du stock

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, MapPin, Phone, User, ArrowLeft, Check, 
  AlertCircle, Loader, Home, CreditCard, Truck, Shield
} from 'lucide-react';
import { cartAPI, ordersAPI, authAPI } from '../services/api';

export default function Checkout() {
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const user = authAPI.getCurrentUser();

  const [formData, setFormData] = useState({
    delivery_address: '',
    phone: user?.phone || '',
    payment_method: 'cash'
  });

  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      navigate('/login');
      return;
    }
    loadCart();
  }, []);

  const loadCart = async () => {
    setLoading(true);
    try {
      const data = await cartAPI.getCart();
      if (!data.items || data.items.length === 0) {
        alert('Votre panier est vide !');
        navigate('/cart');
        return;
      }
      setCart(data);
    } catch (error) {
      console.error('Erreur chargement panier:', error);
      alert('Erreur lors du chargement du panier');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.delivery_address.trim()) {
      alert('Veuillez saisir votre adresse de livraison');
      return;
    }

    if (!formData.phone.trim()) {
      alert('Veuillez saisir votre numéro de téléphone');
      return;
    }

    setSubmitting(true);

    try {
      // ✅ Créer la commande (déduction automatique du stock côté backend)
      const result = await ordersAPI.create({
        delivery_address: formData.delivery_address,
        phone: formData.phone,
        payment_method: formData.payment_method
      });

      // Succès !
      alert(`✅ Commande #${result.order.id} créée avec succès !\n\nVotre stock a été déduit automatiquement.`);
      
      // Rediriger vers les commandes
      navigate('/orders');
    } catch (error) {
      console.error('Erreur création commande:', error);
      
      if (error.response?.data?.error) {
        alert(`❌ ${error.response.data.error}`);
      } else if (error.response?.data?.details) {
        const details = error.response.data.details
          .map(d => `${d.product}: demandé ${d.requested}, disponible ${d.available}`)
          .join('\n');
        alert(`❌ Stock insuffisant :\n\n${details}`);
      } else {
        alert('❌ Erreur lors de la création de la commande');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/80x80?text=Pas+d%27image';
    if (imagePath.startsWith('http')) return imagePath;
    const baseUrl = 'http://localhost:8000';
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${baseUrl}${cleanPath}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <div className="text-xl text-gray-300">Chargement...</div>
        </div>
      </div>
    );
  }

  const cartItems = cart?.items || [];
  const subtotal = cart?.total || 0;
  const shipping = 2000;
  const total = subtotal + shipping;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Formes décoratives */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-white/10 border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/cart')}
                className="p-2 text-white hover:text-orange-400 hover:bg-white/10 rounded-xl transition"
              >
                <ArrowLeft size={22} />
              </button>
              <div 
                className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent cursor-pointer"
                onClick={() => navigate('/')}
              >
                MonMarket
              </div>
              <span className="text-white/30">|</span>
              <span className="text-white font-medium hidden sm:block">Finalisation</span>
            </div>
            <button 
              onClick={() => navigate('/')}
              className="p-2 text-white hover:text-orange-400 hover:bg-white/10 rounded-xl transition"
            >
              <Home size={22} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 relative z-10">
        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Formulaire de livraison */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Informations de livraison */}
            <div className="bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl p-8 border border-white/20">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
                <MapPin className="text-orange-400" size={28} />
                <span>Informations de livraison</span>
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Nom complet */}
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Nom complet
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      value={user?.username || ''}
                      disabled
                      className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                    />
                  </div>
                </div>

                {/* Téléphone */}
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Numéro de téléphone *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="Ex: +225 07 XX XX XX XX"
                      required
                      className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>

                {/* Adresse de livraison */}
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Adresse de livraison *
                  </label>
                  <textarea
                    value={formData.delivery_address}
                    onChange={(e) => setFormData({...formData, delivery_address: e.target.value})}
                    placeholder="Entrez votre adresse complète (quartier, rue, indication...)"
                    required
                    rows={4}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  />
                </div>

                {/* Mode de paiement avec logos officiels */}
<div>
  <label className="block text-sm font-semibold text-gray-300 mb-3">
    Mode de paiement *
  </label>
  <div className="space-y-3">
    
    {/* Paiement à la livraison */}
    <label className="flex items-center p-4 bg-white/5 border-2 border-white/10 rounded-xl cursor-pointer hover:bg-white/10 transition group hover:border-green-500/50">
      <input
        type="radio"
        name="payment_method"
        value="cash"
        checked={formData.payment_method === 'cash'}
        onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
        className="mr-3 w-4 h-4 accent-orange-500"
      />
      <div className="w-14 h-14 bg-green-500/20 rounded-xl mr-3 flex items-center justify-center border border-green-500/30 flex-shrink-0">
        <CreditCard className="text-green-400" size={28} />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-white font-bold block">Paiement à la livraison</span>
        <span className="text-gray-400 text-xs">Payez en espèces lors de la réception</span>
      </div>
    </label>
    
    {/* MTN Money avec logo officiel */}
    <label className="flex items-center p-4 bg-white/5 border-2 border-white/10 rounded-xl cursor-pointer hover:bg-white/10 transition group hover:border-yellow-500/50">
      <input
        type="radio"
        name="payment_method"
        value="mtn"
        checked={formData.payment_method === 'mtn'}
        onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
        className="mr-3 w-4 h-4 accent-orange-500"
      />
      <div className="w-14 h-14 bg-white rounded-xl mr-3 flex items-center justify-center p-2 flex-shrink-0">
        <img 
          src="/images/payment/mtn-logo.png"
          alt="MTN Money"
          className="w-full h-full object-contain"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.classList.add('bg-yellow-400');
            e.target.parentElement.innerHTML = '<span class="text-black font-black text-xl">M</span>';
          }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-white font-bold block">MTN Mobile Money</span>
        <span className="text-gray-400 text-xs">Paiement sécurisé via MTN MoMo</span>
      </div>
      <div className="hidden sm:block text-yellow-400 font-bold text-xs bg-yellow-400/10 px-2 py-1 rounded-lg flex-shrink-0">
        Populaire
      </div>
    </label>
    
    {/* Orange Money avec logo officiel */}
    <label className="flex items-center p-4 bg-white/5 border-2 border-white/10 rounded-xl cursor-pointer hover:bg-white/10 transition group hover:border-orange-500/50">
      <input
        type="radio"
        name="payment_method"
        value="orange"
        checked={formData.payment_method === 'orange'}
        onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
        className="mr-3 w-4 h-4 accent-orange-500"
      />
      <div className="w-14 h-14 bg-white rounded-xl mr-3 flex items-center justify-center p-2 flex-shrink-0">
        <img 
          src="/images/payment/orange-logo.png"
          alt="Orange Money"
          className="w-full h-full object-contain"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.classList.add('bg-gradient-to-br', 'from-orange-500', 'to-orange-600');
            e.target.parentElement.innerHTML = '<span class="text-white font-black text-xl">O</span>';
          }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-white font-bold block">Orange Money</span>
        <span className="text-gray-400 text-xs">Paiement sécurisé via Orange Money</span>
      </div>
    </label>
    
    {/* Wave avec logo officiel */}
    <label className="flex items-center p-4 bg-white/5 border-2 border-white/10 rounded-xl cursor-pointer hover:bg-white/10 transition group hover:border-blue-500/50">
      <input
        type="radio"
        name="payment_method"
        value="wave"
        checked={formData.payment_method === 'wave'}
        onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
        className="mr-3 w-4 h-4 accent-orange-500"
      />
      <div className="w-14 h-14 bg-white rounded-xl mr-3 flex items-center justify-center p-2 flex-shrink-0">
        <img 
          src="/images/payment/wave-logo.png"
          alt="Wave"
          className="w-full h-full object-contain"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.classList.add('bg-gradient-to-br', 'from-blue-700', 'to-blue-900');
            e.target.parentElement.innerHTML = '<span class="text-white font-black text-xl">W</span>';
          }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-white font-bold block">Wave</span>
        <span className="text-gray-400 text-xs">Paiement rapide via Wave CI</span>
      </div>
      <div className="hidden sm:block text-blue-400 font-bold text-xs bg-blue-400/10 px-2 py-1 rounded-lg flex-shrink-0">
        Nouveau
      </div>
    </label>

    {/* Moov Money avec logo officiel */}
    <label className="flex items-center p-4 bg-white/5 border-2 border-white/10 rounded-xl cursor-pointer hover:bg-white/10 transition group hover:border-blue-500/50">
      <input
        type="radio"
        name="payment_method"
        value="moov"
        checked={formData.payment_method === 'moov'}
        onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
        className="mr-3 w-4 h-4 accent-orange-500"
      />
      <div className="w-14 h-14 bg-white rounded-xl mr-3 flex items-center justify-center p-2 flex-shrink-0">
        <img 
          src="/images/payment/moov-logo.png"
          alt="Moov Money"
          className="w-full h-full object-contain"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.classList.add('bg-gradient-to-br', 'from-blue-600', 'to-blue-700');
            e.target.parentElement.innerHTML = '<span class="text-white font-black text-lg">moov</span>';
          }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-white font-bold block">Moov Money</span>
        <span className="text-gray-400 text-xs">Paiement sécurisé via Moov Africa</span>
      </div>
    </label>
    
  </div>
</div>


                {/* Bouton de soumission */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-2xl font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {submitting ? (
                    <>
                      <Loader className="animate-spin" size={24} />
                      <span>Création en cours...</span>
                    </>
                  ) : (
                    <>
                      <Check size={24} />
                      <span>Confirmer la commande</span>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Garanties */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/10 backdrop-blur-2xl rounded-2xl p-4 border border-white/20 text-center">
                <Shield className="mx-auto text-green-400 mb-2" size={32} />
                <div className="text-sm font-semibold text-white">Paiement sécurisé</div>
              </div>
              <div className="bg-white/10 backdrop-blur-2xl rounded-2xl p-4 border border-white/20 text-center">
                <Truck className="mx-auto text-blue-400 mb-2" size={32} />
                <div className="text-sm font-semibold text-white">Livraison rapide</div>
              </div>
              <div className="bg-white/10 backdrop-blur-2xl rounded-2xl p-4 border border-white/20 text-center">
                <Check className="mx-auto text-orange-400 mb-2" size={32} />
                <div className="text-sm font-semibold text-white">Garantie 2 ans</div>
              </div>
            </div>
          </div>

          {/* Résumé de commande */}
          <div className="lg:col-span-1">
            <div className="bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl p-6 sticky top-24 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center space-x-2">
                <ShoppingCart className="text-orange-400" size={24} />
                <span>Résumé</span>
              </h3>
              
              {/* Articles */}
              <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex items-center space-x-3">
                    <img 
                      src={getImageUrl(item.product?.image)}
                      alt={item.product?.name}
                      className="w-16 h-16 rounded-xl object-cover border border-white/20"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white truncate">
                        {item.product?.name}
                      </div>
                      <div className="text-xs text-gray-400">
                        {item.quantity} × {item.product?.price?.toLocaleString()} CFA
                      </div>
                    </div>
                    <div className="text-sm font-bold text-white">
                      {item.subtotal?.toLocaleString()} CFA
                    </div>
                  </div>
                ))}
              </div>

              {/* Totaux */}
              <div className="space-y-3 border-t border-white/20 pt-4">
                <div className="flex justify-between text-gray-300">
                  <span>Sous-total</span>
                  <span className="font-semibold text-white">{subtotal?.toLocaleString()} CFA</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Livraison</span>
                  <span className="font-semibold text-white">{shipping.toLocaleString()} CFA</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-white pt-3 border-t border-white/20">
                  <span>Total</span>
                  <span className="text-orange-400">{total.toLocaleString()} CFA</span>
                </div>
              </div>

              {/* Alerte info */}
              <div className="mt-6 p-4 bg-blue-500/20 backdrop-blur-md rounded-2xl border border-blue-400/30">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={20} />
                  <div className="text-sm text-blue-200">
                    En validant, le stock sera automatiquement déduit et votre commande sera envoyée au vendeur.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}